import { CognitoUser } from "../types/apiService.types";

const BASE_URL = process.env.REACT_APP_PROMETHEUS_API_BASE_URL;

if (!BASE_URL) {
  throw new Error("REACT_APP_PROMETHEUS_API_BASE_URL is not defined");
}

const getUser = (): CognitoUser | null => {
  try {
    const key = Object.keys(sessionStorage).find((k) => k.startsWith("oidc.user:"));
    if (!key) return null;

    const raw = sessionStorage.getItem(key);
    if (!raw) return null;

    return JSON.parse(raw) as CognitoUser;
  } catch {
    return null;
  }
};

export interface ApiFetchInit extends RequestInit {
  // Skip the default JSON Content-Type - needed for FormData bodies, where the
  // browser has to set the header itself so it can add the multipart boundary
  omitContentType?: boolean;
}

export const apiFetch = (url: string, init?: ApiFetchInit): Promise<Response> => {
  const user = getUser();
  const cleanBase = BASE_URL.replace(/\/+$/, "");
  const cleanPath = url.replace(/^\/+/, "");
  const { omitContentType, ...requestInit } = init ?? {};

  const headers = new Headers();
  if (!omitContentType) {
    headers.set("Content-Type", "application/json");
  }

  // Passed headers overwrite defaults
  if (requestInit.headers) {
    new Headers(requestInit.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }

  // FormData bodies need the browser to set Content-Type so it includes the multipart boundary
  if (typeof FormData !== "undefined" && init?.body instanceof FormData) {
    headers.delete("Content-Type");
  }

  if (user?.access_token) {
    headers.set("Authorization", `Bearer ${user.access_token}`);
  }

  return fetch(`${cleanBase}/${cleanPath}`, {
    ...requestInit,
    headers,
  });
};

export class HttpError extends Error {
  response: { status: number; data: unknown };
  isHttpError = true;

  constructor(status: number, data: unknown, message?: string) {
    super(message ?? `Request failed with status ${String(status)}`);
    this.name = "HttpError";
    this.response = { status, data };
  }
}

export const isHttpError = (error: unknown): error is HttpError => error instanceof HttpError;

// Turn a thrown error into a message we can show the user. The middleware returns a body like
// { ErrorCode: "NO_DATA_FOUND", ErrorReason: "No data found for given parameters" } alongside
// error statuses, so prefer that reason when present before falling back to a generic message.
export const getApiErrorMessage = (error: unknown, notFoundMessage: string): string => {
  if (isHttpError(error)) {
    const { status, data } = error.response;
    const apiReason =
      data && typeof data === "object" && "ErrorReason" in data
        ? String(data.ErrorReason)
        : undefined;

    if (status === 404) return apiReason ?? notFoundMessage;
    if (status === 500 || status === 502) return "Server is down. Please try again later.";
    return apiReason ?? `Server error (${String(status)}). Please try again.`;
  }
  return "An error occurred while searching. Please try again.";
};

type ApiResponseType = "json" | "text" | "blob";

interface ApiRequestConfig {
  headers?: HeadersInit;
  responseType?: ApiResponseType;
}

const parseBody = async (response: Response, responseType: ApiResponseType): Promise<unknown> => {
  try {
    if (responseType === "text") return await response.text();
    if (responseType === "blob") return await response.blob();
    return await response.json();
  } catch {
    return null;
  }
};

const request = async <T>(
  url: string,
  init: RequestInit | undefined,
  responseType: ApiResponseType,
): Promise<T> => {
  const response = await apiFetch(url, init);
  const data = await parseBody(response, responseType);

  if (!response.ok) {
    throw new HttpError(response.status, data);
  }

  return data as T;
};

// GET/POST helpers that mirror the axios-based `api.get`/`api.post` they replace:
// auto-parse the response body, resolve with it directly, and throw an `HttpError`
// (mirroring axios's `error.response.status`/`.data` shape) on a non-2xx response.
export const apiGet = <T>(url: string, config?: ApiRequestConfig): Promise<T> =>
  request<T>(url, { method: "GET", headers: config?.headers }, config?.responseType ?? "json");

export const apiPost = <T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T> => {
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;

  return request<T>(
    url,
    {
      method: "POST",
      headers: config?.headers,
      body: isFormData ? data : data !== undefined ? JSON.stringify(data) : undefined,
    },
    config?.responseType ?? "json",
  );
};

export const apiPut = <T>(url: string, data?: unknown, config?: ApiRequestConfig): Promise<T> =>
  request<T>(
    url,
    {
      method: "PUT",
      headers: config?.headers,
      body: data !== undefined ? JSON.stringify(data) : undefined,
    },
    config?.responseType ?? "json",
  );

export const apiDelete = <T>(url: string, config?: ApiRequestConfig): Promise<T> =>
  request<T>(url, { method: "DELETE", headers: config?.headers }, config?.responseType ?? "json");

export default apiFetch;
